// src/pages/VerifyEmailPage.js

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { authAPI } from "../api/authApi";
import ErrorBanner from "../ui/ErrorBanner";
import { consumeRedirectAfterAuth } from "../utils/redirectAfterAuth";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();

  // Get URL params (from email deep-link: /verify-email?code=842719&email=user@example.com)
  const urlCode = searchParams.get("code");
  const urlEmail = searchParams.get("email");

  // Email from navigation state (normal signup flow) or URL params (email link flow)
  const email = location.state?.email || urlEmail;

  // Redirect to signup if no email provided
  useEffect(() => {
    if (!email) {
      navigate("/signup");
    }
  }, [email, navigate]);

  // State for 6-digit code
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Refs for each input
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Auto-fill and auto-submit when arriving via email deep-link
  useEffect(() => {
    if (urlCode && urlCode.length === 6 && /^\d{6}$/.test(urlCode)) {
      const digits = urlCode.split("");
      setCode(digits);
      // Small delay to let React render the filled inputs before submitting
      const timer = setTimeout(() => {
        handleVerify(urlCode);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [urlCode]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  /**
   * Handle input change for a single digit
   */
  const handleChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1);
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    // update code array
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit if all 6 digits filled
    if (index === 5 && value && newCode.every((digit) => digit)) {
      handleVerify(newCode.join(""));
    }
  };

  /**
   * Handle backspace/delete
   */
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Move to previous input if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Handle paste event
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setCode(newCode);
      setError(null);

      // Focus last input
      inputRefs.current[5]?.focus();

      // Auto-submit
      handleVerify(pastedData);
    }
  };

  /**
   * Verify the code
   */
  const handleVerify = async (codeString) => {
    const finalCode = codeString || code.join("");
    if (finalCode.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await verifyEmail(finalCode);
      // Consume the intended destination (works for both the signup->verify flow
      // and the direct /verify-email?code=... email deep-link); default /home.
      navigate(consumeRedirectAfterAuth() || "/home", { replace: true });
    } catch (error) {
      setError(error.message || "Invalid verification code. Please try again");
      // Clear code on error
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resend verification
   */
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError(null);

    try {
      await authAPI.sendVerificationCode(email);
      setResendCooldown(60); // 60 second cooldown
      // Show success message briefly
      setError(null);
    } catch (error) {
      setError(error.message || "Failed to resend code. Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-elevated px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="text-3xl font-bold text-text-primary">
            Traces
          </a>
          <p className="text-sm text-text-muted mt-1">Verify your email</p>
        </div>

        <div className="bg-surface-base border border-border-default rounded-lg p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Enter verification code
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            We sent a 6-digit code to{" "}
            <span className="font-medium text-text-primary">{email}</span>
          </p>

          {/* 6-digit code inputs */}
          <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-semibold bg-surface-elevated border border-border-strong rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                disabled={loading}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <ErrorBanner
              message={error}
              onDismiss={() => setError(null)}
              className="mb-4"
            />
          )}

          {/* Verify button */}
          <button
            onClick={() => handleVerify()}
            disabled={loading || code.some((digit) => !digit)}
            className="w-full bg-primary text-text-inverse py-2 px-4 rounded-md text-sm font-medium hover:bg-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            {loading ? "Verifying..." : "Verify"}
          </button>

          {/* Resend code */}
          <div className="text-center text-sm text-text-secondary">
            Didn't receive the code?{" "}
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0 || loading}
              className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend code"}
            </button>
          </div>
        </div>

        {/* Back to signup */}
        <div className="text-center text-sm text-text-secondary mt-6">
          <a href="/signup" className="text-primary hover:underline">
            Back to sign up
          </a>
        </div>
      </div>
    </div>
  );
}
